import classnames from 'classnames';
import React from 'react';
import './BehavioralPatternBar.scss';
import BehavioralPatternShape from './BehavioralPatternShape';
import FactorShapeService, {
  BAFactorShapeType,
  BehavioralPatternGraphType,
  behavioralPatternGraphs,
} from './FactorShape.service';

/**
 * The name of the sigma. This is only used for visual purposes and doesn't
 * play a part in further calculations.
 */
export enum BASigmaName {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',
}

export type BehavioralPatternBarProps = {
  sigmaValue: number;
  /**
   * An optional second sigma value which can be displayed on the bar. For
   * example, when two BA patterns are being compared.
   */
  secondSigmaValue?: number;
  /**
   * If provided, it will create a line between the current sigma value and
   * the previous sigma value. This can happen for the second sigma value as well
   * if that is provided.
   *
   * The previous sigma value is used instead of the next so that the line
   * is drawn over the bar instead of under.
   */
  connectingLineInfo?: {
    previousSigmaValue: number;
    secondPreviousSigmaValue?: number;
  };
  /**
   * If not provided, defaults to a circle. If two sigma values are provided,
   * this is ignored, as the first sigma value will always be displayed as a
   * square, and the second will be a circle.
   */
  shapeType?: BAFactorShapeType;
  graphType: BehavioralPatternGraphType;
  sigmaName: BASigmaName;
};

/**
 * A generalized behavioral pattern bar that can be used to display a single
 * or two sigma values.
 *
 * This is expected to be on a white background.
 */
const BehavioralPatternBar = ({
  sigmaValue,
  secondSigmaValue,
  sigmaName,
  graphType,
  shapeType,
  connectingLineInfo,
}: BehavioralPatternBarProps): React.JSX.Element => {
  const baseClassName = 'BehavioralPatternBar';
  const graphInfo = behavioralPatternGraphs[graphType];

  // Basic bar calculations
  const isESigma = sigmaName === BASigmaName.E;
  const factorBarYMidpoint = isESigma ? 1 : graphInfo.height / 2;
  const nextFactorBarYMidpoint = factorBarYMidpoint + graphInfo.height + graphInfo.spacing;
  const viewBox = `0 0 ${graphInfo.width} ${nextFactorBarYMidpoint}`;
  const factorBarHeight = isESigma ? graphInfo.eSigmaBarHeight : graphInfo.height;

  // Midline calculations
  const midlineWidth = graphInfo.eSigmaBarHeight;
  const midlineHeight = sigmaName === BASigmaName.D ? factorBarHeight : factorBarHeight + graphInfo.spacing;
  const midlineStartingX = graphInfo.width / 2 - midlineWidth / 2;

  /**
   * The midline is the line that goes through the very center of the pattern
   * bar.
   *
   * - `M` determines the starting point on the X axis of the line
   * - `h` determines the width at the top of the line
   * - `h -<value>z` determines the width at the bottom of the line
   * - `v` determines the height of the line
   */
  const midline = isESigma ? null : (
    <path
      className={`${baseClassName}__factor-midline`}
      d={`M ${midlineStartingX} 0 h ${midlineWidth} v ${midlineHeight} h -${midlineWidth}z`}
    />
  );

  // Sigma markers
  const sigmaMarkers =
    graphType === BehavioralPatternGraphType.Mini || isESigma
      ? null
      : Array.from({ length: 7 }).map((_, index) => {
          const x = (index * (graphInfo.width - graphInfo.height)) / 6 + graphInfo.height / 2;
          const y1 = index === 3 ? 0 : (2 * graphInfo.height) / 3;
          return (
            <line
              key={`sigma-marker-${index}`}
              className={`${baseClassName}__sigma-marker`}
              x1={x}
              y1={y1}
              x2={x}
              y2={graphInfo.height}
            />
          );
        });

  // Shapes
  function getSvgShape(sigmaVal: number, shapeType: BAFactorShapeType) {
    const centerX = FactorShapeService.getFactorShapeXValue(sigmaVal, graphInfo, shapeType);
    const xCoord = shapeType === BAFactorShapeType.Circle ? centerX : centerX - graphInfo.squareWidth / 2;
    const yCoord =
      shapeType === BAFactorShapeType.Circle ? factorBarYMidpoint : factorBarYMidpoint - graphInfo.squareWidth / 2;
    return <BehavioralPatternShape graphType={graphType} shapeType={shapeType} coords={{ x: xCoord, y: yCoord }} />;
  }
  const firstShapeType =
    secondSigmaValue || secondSigmaValue === 0 ? BAFactorShapeType.Square : shapeType ?? BAFactorShapeType.Circle;
  const shape1 = getSvgShape(sigmaValue, firstShapeType);
  const shape2 =
    secondSigmaValue || secondSigmaValue === 0 ? getSvgShape(secondSigmaValue, BAFactorShapeType.Circle) : null;

  // Lines connecting Sigmas
  function getSvgLine(sigmaVal: number, previousSigmaVal: number, shapeType: BAFactorShapeType) {
    const xValue = FactorShapeService.getFactorShapeXValue(sigmaVal, graphInfo, shapeType);
    const previousXValue = FactorShapeService.getFactorShapeXValue(previousSigmaVal, graphInfo, shapeType);
    const previousFactorBarYMidpoint = factorBarYMidpoint - graphInfo.height - graphInfo.spacing;
    const shapeClass = shapeType === BAFactorShapeType.Circle ? 'circle' : 'square';
    return (
      <line
        className={`${baseClassName}__factor-${shapeClass}-line`}
        strokeWidth={graphInfo.strokeWidth}
        x1={xValue}
        y1={factorBarYMidpoint}
        x2={previousXValue}
        y2={previousFactorBarYMidpoint}
      />
    );
  }
  const connectingLine1 = connectingLineInfo
    ? getSvgLine(sigmaValue, connectingLineInfo.previousSigmaValue, firstShapeType)
    : null;
  const connectingLine2 =
    connectingLineInfo &&
    (connectingLineInfo.secondPreviousSigmaValue || connectingLineInfo.secondPreviousSigmaValue === 0)
      ? getSvgLine(secondSigmaValue, connectingLineInfo.secondPreviousSigmaValue, BAFactorShapeType.Circle)
      : null;

  function getSvgFactorLetter(sigmaVal: number, shapeType: BAFactorShapeType) {
    if (graphType === BehavioralPatternGraphType.Mini) {
      return null;
    }
    const xCoord = FactorShapeService.getFactorShapeXValue(sigmaVal, graphInfo, shapeType);
    const yCoord = factorBarYMidpoint + graphInfo.height / 4;
    return (
      <text className={`${baseClassName}__factor-letter`} x={xCoord} y={yCoord}>
        {sigmaName}
      </text>
    );
  }
  const shape1FactorLetter =
    graphType === BehavioralPatternGraphType.Mini ? null : getSvgFactorLetter(sigmaValue, firstShapeType);
  const shape2FactorLetter =
    graphType === BehavioralPatternGraphType.Mini
      ? null
      : getSvgFactorLetter(secondSigmaValue, BAFactorShapeType.Circle);

  const containerClasses = classnames(baseClassName, {
    [`${baseClassName}--e-sigma`]: isESigma,
  });

  return (
    <div className={containerClasses} style={{ width: graphInfo.width, height: graphInfo.height }}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox={viewBox} className={`${baseClassName}__factor-range-svg`}>
        <rect className={`${baseClassName}__factor-bar`} style={{ height: factorBarHeight }} rx={factorBarHeight / 2} />
        {sigmaMarkers}
        {midline}
        {/* Connecting lines go before shapes so that the shapes are on top */}
        {connectingLine2}
        {connectingLine1}
        {/* Shape 2 goes before shape 1 so that shape 1 is on top */}
        {shape2}
        {shape2FactorLetter}
        {shape1}
        {shape1FactorLetter}
      </svg>
    </div>
  );
};

export default BehavioralPatternBar;
